import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-column-settings-menu',
  standalone: false,
  templateUrl: './column-settings-menu.component.html',
  styleUrl: './column-settings-menu.component.scss'
})
export class ColumnSettingsMenuComponent implements OnInit, OnChanges {
  @Input() columns: any[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() exportColumns: Set<string> = new Set();
  @Input() hasExportOptions: boolean = false;
  @Input() updateOnClose: boolean = false;
  // Added by amisha on 2026-06-01 to support screen-specific limits for visible columns.
  // Reason: the menu must stop the 8th selection before it is saved back to the table state.
  @Input() maxVisibleColumns: number | null = null;
  @Input() maxVisibleColumnsMessage: string = '';

  @Output() toggleColumn = new EventEmitter<string>();
  @Output() toggleExport = new EventEmitter<string>();
  @Output() visibleColumnLimitReached = new EventEmitter<string>();
  @Output() displayedColumnsUpdated = new EventEmitter<string[]>(); // Code added by Sanchit on 07/14/2026

  @ViewChild('menu') menu!: MatMenu;
  // added by priya 17-04-2026
  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
  openMenu() {
    this.menuTrigger?.openMenu();
  }
  
  closeMenu() {
    this.menuTrigger?.closeMenu();
  }
    // end by priya 17-04-2026

  // added by furkan 02-04-2026
  localDisplayedColumns: string[] = [];
  localExportColumns: Set<string> = new Set();

  ngOnInit() {
    this.syncLocalState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['displayedColumns'] || changes['exportColumns']) {
      this.syncLocalState();
    }
  }

  // Syncs the parent table's state into the menu's local state
  private syncLocalState() {
    this.localDisplayedColumns = [...this.displayedColumns];
    this.localExportColumns = new Set(this.exportColumns);
  }

  // ended by furkan 02-04-2026

  // update by furkan for multiple column selection
  handleColClick(event: Event, c: any) {
    event.stopPropagation();
    event.preventDefault();
    if (c.hideable === false) return; // Prevent toggling if disabled
    this.onToggleColumn(c.key);
  }

  handleExpClick(event: Event, c: any) {
    event.stopPropagation();
    event.preventDefault();
    this.onToggleExport(c.key);
  }

  onToggleColumn(key: string) {
    const index = this.localDisplayedColumns.indexOf(key);

    // Added by amisha on 2026-06-01 to reject an 8th visible column before it reaches the parent save flow.
    // Reason: prevents invalid menu-close saves and lets the host screen show the shared warning modal.
    if (
      index === -1 &&
      this.maxVisibleColumns !== null &&
      this.getVisibleColumnCount([...this.localDisplayedColumns, key]) >
        this.maxVisibleColumns
    ) {
      this.visibleColumnLimitReached.emit(this.getMaxVisibleColumnsMessage());
      return;
    }

    if (index > -1) {
      this.localDisplayedColumns.splice(index, 1);
    } else {
      this.localDisplayedColumns.push(key);
    }

    if (!this.updateOnClose) {
      this.toggleColumn.emit(key);
    }
  }

  onToggleExport(key: string) {
    if (this.localExportColumns.has(key)) {
      this.localExportColumns.delete(key);
    } else {
      this.localExportColumns.add(key);
    }

    if (!this.updateOnClose) {
      this.toggleExport.emit(key);
    }
  }

  onMenuClosed() {
    if (!this.updateOnClose) {
      return;
    }

    // 1. Process Displayed Columns
    const added = this.localDisplayedColumns.filter(x => !this.displayedColumns.includes(x));
    const removed = this.displayedColumns.filter(x => !this.localDisplayedColumns.includes(x));

    added.forEach(key => this.toggleColumn.emit(key));
    removed.forEach(key => this.toggleColumn.emit(key));

    // Emit final displayed columns once
    this.displayedColumnsUpdated.emit([...this.localDisplayedColumns]); // Code added by Sanchit on 07/14/2026

    // 2. Process Export Columns
    const addedExport = Array.from(this.localExportColumns).filter(x => !this.exportColumns.has(x));
    const removedExport = Array.from(this.exportColumns).filter(x => !this.localExportColumns.has(x));

    addedExport.forEach(key => this.toggleExport.emit(key));
    removedExport.forEach(key => this.toggleExport.emit(key));
  }

  // Added by amisha on 2026-06-01 to count only user-toggleable visible columns.
  // Reason: hidden-menu utility columns should not consume the Janus 7-column limit.
  private getVisibleColumnCount(displayedColumns: string[]): number {
    const visibleKeys = new Set(displayedColumns);

    return (this.columns ?? []).filter(
      column => column.hideMenu !== true && visibleKeys.has(column.key)
    ).length;
  }

  // Added by amisha on 2026-06-01 to keep the warning text configurable per screen.
  // Reason: shared menu logic should reuse a screen-provided message when one is supplied.
  private getMaxVisibleColumnsMessage(): string {
    if (this.maxVisibleColumnsMessage.trim()) {
      return this.maxVisibleColumnsMessage;
    }

    return `You can only show ${this.maxVisibleColumns} columns at once.`;
  }
}
// ended by furkan 02-04-2026
